import { FC, useEffect } from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Divider,
  FormGroup,
  Switch,
  Typography
} from '@material-ui/core';
import { useDispatch, useSelector } from "react-redux";
import { InputField } from '../input-field';
import { MentionInput } from './mention-input';
import { ConfirmationDialog }  from '../confirmation-dialog';
import { commentPost, commentUpdate, resetCommentStatus } from '../../slices/commentSlice';
import { RootState } from '../../store';
import { useLocation } from 'react-router-dom';
import { getActiveElement } from '../../utils/active-element';
import { screenLabelFromParentType } from '../../hooks/use-screen-context';
import { useTranslation } from 'react-i18next';
import { lightSuccess } from '../../colors';

// Private = pink, shared = green (same values as the Lists comment filters).
const PRIVACY_PINK = '#E91E63';

interface CommentDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CommentDialog: FC<CommentDialogProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose, ...other } = props;
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [mentions, setMentions] = useState<any[]>([]);
  const comment = useSelector((state: RootState) => state.comment);
  const dispatch = useDispatch();

  const location = useLocation();
  const activeElement = getActiveElement(location.pathname);
  const currentParentType = activeElement.parentType;
  // Edit mode when an existing comment id is loaded (same condition as the
  // prefill below); a duplicate keeps commentId null and stays a creation.
  const isEditing = comment.commentId !== null && comment.commentId !== undefined;

  // The screen the comment will be attached to. Defaults to the current page, but
  // (unlike the Documents drawer selector) can be changed to actually re-target
  // the created comment to another screen.
  const [selectedParentType, setSelectedParentType] = useState<string | null>(currentParentType);

  // Screens offered in the selector: those that already have comments in the
  // project (from the loaded list — the comment slice keeps them, same principle
  // as the Documents drawer) plus the current page.
  const screenOptions = Array.from(
    new Set(
      (comment.comments || [])
        .map((c: any) => c.parentType)
        .filter(Boolean)
        .concat(currentParentType ? [currentParentType] : [])
    )
  ).sort();

  let initialValues = {
    message: '',
    title: '',
    importance: 0,
    url: '',
    isShared: false,
    submit: null
  }

  if ((comment.commentId !== null && comment.commentId !== undefined) || comment.isDuplicated === true) {
    initialValues.message = comment.message;
    initialValues.title = comment.title;
    initialValues.importance = comment.importance;
    initialValues.url = comment.url;
    initialValues.isShared = comment.isShared;
  }

  useEffect(() => {
    if (comment.commentStatus === 'success') {
      toast.success(isEditing ? t('comment_updated') : t('comment_created'));
      formik.resetForm();
      setMentions([]);
      formik.setStatus({ success: true });
      formik.setSubmitting(false);
      dispatch(resetCommentStatus());
      if (comment.commentNextAction === 'close') {
        onClose();
      }
    } else {
      formik.setStatus({ success: false });
      formik.setErrors({ submit: comment.commentStatus });
      formik.setSubmitting(false);
    }
  
  }, [comment.commentStatus]);

  // Reopening the dialog resets the screen selector back to the current page.
  useEffect(() => {
    if (open) {
      setSelectedParentType(activeElement.parentType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object().shape({
      message: Yup.string().min(3).required('Message is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        // dispatch(review1PostCustomer(values));
      } catch (err) {
        console.error(err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    }
  });

  const handleIsShared = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('isShared', event.target.checked);
  };

  const handleSubmit = (nextAction = 'close') => {
    // Auto-prefix the protocol so a pasted "example.com" stays a valid href.
    let url = (formik.values.url || '').trim();
    if (url && !/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    // Editing must PUT, not POST — the dialog said "Update" but posted, so every
    // edit created a second comment instead of changing the original. status is
    // carried through so saving an archived comment doesn't make it current.
    if (isEditing) {
      dispatch(commentUpdate(nextAction, comment.commentId, {
        ...formik.values,
        url,
        importance: Number(formik.values.importance),
        status: comment.status,
      }));
      return;
    }
    dispatch(commentPost(nextAction, {
      ...formik.values,
      url,
      importance: Number(formik.values.importance),
      mentions,
      // Attach to the selected screen. parent_id only carries over when the
      // selection is still the current page (other screens have no known item id).
      parentType: selectedParentType,
      parentId: selectedParentType === activeElement.parentType ? activeElement.parentId : null,
    }));
  };
  const handleSubmitAndQuit = () => handleSubmit('close');

  // The buttons call handleSubmit directly rather than going through formik, so
  // the schema's min(3) has to be enforced here — otherwise an empty message
  // would now blank an existing comment rather than just create a junk one.
  const submitDisabled = comment.commentIsLoading
    || !comment.isEditabled
    || (formik.values.message || '').trim().length < 3;

  return (
    <Dialog
      // maxWidth="md"
      fullWidth
      onClose={() => {setOpenConfirmationDialog(true)}}
      open={open}
      TransitionProps={{
        onExited: () => formik.resetForm()
      }}
      {...other}
    >
      <DialogTitle sx={{ backgroundColor: "background.default" }}>
        {isEditing ? t('update_comment') : t('create_comment')}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "background.default" }}>
        <Grid
          container
          spacing={2}
          my={2}
        >
          {/* Creation only: PUT /comments/:id cannot change parent_type, so offering
              the selector while editing would silently discard the change. */}
          {!isEditing && screenOptions.length > 0 && (
            <Grid
              item
              xs={12}
            >
              <InputField
                fullWidth
                select
                SelectProps={{ native: true }}
                label={t('screen')}
                value={selectedParentType ?? ''}
                onChange={(e) => setSelectedParentType(e.target.value || null)}
              >
                {screenOptions.map((pt) => (
                  <option key={pt} value={pt}>
                    {screenLabelFromParentType(pt) || pt}
                  </option>
                ))}
              </InputField>
            </Grid>
          )}
          <Grid
            item
            xs={12}
          >
            <MentionInput
              value={formik.values.message}
              mentions={mentions}
              onChange={(text, m) => {
                formik.setFieldValue('message', text);
                setMentions(m);
              }}
              placeholder={t('message')}
              rows={4}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <InputField
              fullWidth
              select
              SelectProps={{ native: true }}
              label={t('importance')}
              name="importance"
              onChange={formik.handleChange}
              value={formik.values.importance}
            >
              <option value={0}>{t('importance_not')}</option>
              <option value={1}>{t('importance_important')}</option>
              <option value={2}>{t('importance_very')}</option>
            </InputField>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <InputField
              error={Boolean(formik.touched.url && formik.errors.url)}
              fullWidth
              helperText={formik.touched.url && formik.errors.url}
              label={t('comment_url_label')}
              name="url"
              placeholder="https://example.com"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.url}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            <FormGroup>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: formik.values.isShared ? 'text.secondary' : 'text.primary' }}>
                  {t('comment_private_label')}
                </Typography>
                {/* The track carries the privacy meaning — pink when private,
                    green when shared — instead of relying only on which flanking
                    label is bold. Same pink/green as the Lists comment filters.
                    The thumb is deliberately left alone: the theme paints every
                    switch thumb white, and this one should not be the exception. */}
                <Switch
                  checked={formik.values.isShared}
                  onChange={handleIsShared}
                  sx={{
                    '& .MuiSwitch-track': { backgroundColor: PRIVACY_PINK },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: lightSuccess.main,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ color: formik.values.isShared ? 'text.primary' : 'text.secondary' }}>
                  {t('comment_shared_label')}
                </Typography>
              </Box>
            </FormGroup>
          </Grid>
          {formik.errors.submit && (
            <Grid
              item
              xs={12}
            >
              <FormHelperText error>
                {formik.errors.submit}
              </FormHelperText>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ backgroundColor: "background.default" }}>
        <Button
          color="primary"
          size="small"
          onClick={onClose}
          variant="outlined"
          fullWidth
        >
          {t('cancel')}
        </Button>
        {!isEditing && (
          <Button
            color="primary"
            size="small"
            disabled={submitDisabled}
            onClick={() => { handleSubmit('new'); }}
            variant="outlined"
            fullWidth
          >
            {t('save_and_new')}
          </Button>
        )}
        <Button
          color="primary"
          size="small"
          // disabled={commentIsLoading}
          // disabled={!(formik.isValid && formik.dirty) || comment.commentIsLoading}
          disabled={submitDisabled}
          onClick={() => { handleSubmitAndQuit(); }}
          variant="contained"
          fullWidth
        >
          {isEditing ? t('update') : t('create')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        message={t('do_you_want_to_close_without_saving')}
        onCancel={() => {setOpenConfirmationDialog(false)}}
        onConfirm={() => {
          setOpenConfirmationDialog(false);
          onClose();
        }}
        open={openConfirmationDialog}
        title={t('confirmation_title')}
        variant='warning'
      />
    </Dialog>
  );
};

CommentDialog.defaultProps = {
  open: false
};

CommentDialog.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func
};
